import { Alert, MenuItem, Stack } from '@mui/material';
import { FormDialog, FormTextField, ValueBadge } from '../../../design-system';
import { CommonCatalogFields, commonDraftValid, type CommonCatalogDraft } from './ReferenceCatalogFields';
import type { ReferenceJobFamily, ReferenceJobProfile } from '../model/types';

export type JobProfileDraft = CommonCatalogDraft & {
  jobFamilyId: string;
};

export type JobProfileEditor = {
  mode: 'create' | 'edit';
  item?: ReferenceJobProfile;
  draft: JobProfileDraft;
};

type JobProfileEditorDialogProps = {
  editor?: JobProfileEditor;
  busy: boolean;
  editorError?: string;
  activeFamilies: ReferenceJobFamily[];
  onClose: () => void;
  onSubmit: () => void;
  onChange: <K extends keyof CommonCatalogDraft>(field: K, value: CommonCatalogDraft[K]) => void;
  onFamilyChange: (jobFamilyId: string) => void;
};

export function JobProfileEditorDialog({
  editor,
  busy,
  editorError,
  activeFamilies,
  onClose,
  onSubmit,
  onChange,
  onFamilyChange,
}: JobProfileEditorDialogProps) {
  return (
    <FormDialog
      open={Boolean(editor)}
      onClose={onClose}
      onSubmit={onSubmit}
      title={editor ? `직무 ${editor.mode === 'create' ? '등록' : '수정'}` : ''}
      cancelLabel="취소"
      submitLabel="변경사항 저장"
      busy={busy}
      busyLabel="저장 중…"
      submitDisabled={!editor || !commonDraftValid(editor.draft, editor.mode) || !editor.draft.jobFamilyId}
      maxWidth="sm"
    >
      {editor ? (
        <Stack spacing={2}>
          {editorError ? (
            <Alert severity="error" role="alert">
              {editorError}
            </Alert>
          ) : null}
          <CommonCatalogFields
            draft={editor.draft}
            mode={editor.mode}
            busy={busy}
            descriptorHelper="구성원에게 배정할 표준 직무 이름입니다."
            onChange={onChange}
          />
          <FormTextField
            select
            required
            label="직군"
            value={editor.draft.jobFamilyId}
            disabled={busy}
            error={!editor.draft.jobFamilyId}
            help="직군은 팀원에게 직접 배정하지 않고 선택한 직무에서 파생됩니다."
            onChange={(event) => onFamilyChange(event.target.value)}
          >
            {activeFamilies.map((family) => (
              <MenuItem key={family.id} value={family.id}>
                <ValueBadge label={family.descriptor} /> · {family.code}
              </MenuItem>
            ))}
          </FormTextField>
        </Stack>
      ) : null}
    </FormDialog>
  );
}
